import { Component, Inject, OnInit } from '@angular/core';

import {
  AbstractControl,
  FormBuilder, FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn, Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { RunSearchFilters } from '../../../core/models/simulation.model';

@Component({
  selector: 'app-run-filter-options-popup',
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatInputModule,
    MatSlideToggleModule,
    MatButtonModule,
    MatCardModule
],
  templateUrl: './run-filter-options-popup.component.html',
  styleUrl: './run-filter-options-popup.component.scss'
})
export class RunFilterOptionsPopupComponent implements OnInit {
  directionsForm!: FormGroup;
  cyclesForm!: FormGroup;
  rangesForm!: FormGroup;

  constructor(
    private readonly fb: FormBuilder,
    private readonly dialogRef: MatDialogRef<RunFilterOptionsPopupComponent>,
    @Inject(MAT_DIALOG_DATA) public data: RunSearchFilters,
  ) {}

  get directionsValue() {
    return this.directionsForm.getRawValue();
  }

  get cyclesValue() {
    return this.cyclesForm.getRawValue();
  }

  get rangesValue() {
    return this.rangesForm.getRawValue();
  }

  get interfacialTensionRangeGroup(): FormGroup {
    return this.rangesForm.get('interfacialTensionRange') as FormGroup;
  }

  get primaryDrainageRangeGroup(): FormGroup {
    return this.rangesForm.get('primaryDrainageRange') as FormGroup;
  }

  get firstImbibitionRangeGroup(): FormGroup {
    return this.rangesForm.get('firstImbibitionRange') as FormGroup;
  }

  get interfacialTensionRangeMinControl(): FormControl {
    return this.rangesForm.get('interfacialTensionRange.min') as FormControl;
  }

  get interfacialTensionRangeMaxControl(): FormControl {
    return this.rangesForm.get('interfacialTensionRange.max') as FormControl;
  }

  get primaryDrainageRangeMinControl(): FormControl {
    return this.rangesForm.get('primaryDrainageRange.min') as FormControl;
  }

  get primaryDrainageRangeMaxControl(): FormControl {
    return this.rangesForm.get('primaryDrainageRange.max') as FormControl;
  }

  get firstImbibitionRangeMinControl(): FormControl {
    return this.rangesForm.get('firstImbibitionRange.min') as FormControl;
  }

  get firstImbibitionRangeMaxControl(): FormControl {
    return this.rangesForm.get('firstImbibitionRange.max') as FormControl;
  }

  ngOnInit(): void {
    this.initDirectionsForm();
    this.initCyclesForm();
    this.initRangesForm();
  }

  onConfirm(): void {
    const selectedDirections: string[] = Object.entries(this.directionsValue)
      .filter(([_, value]) => !!value)
      .map(([key, _]) => key);

    const selectedCycles: string[] = Object.entries(this.cyclesValue)
      .filter(([_, value]) => !!value)
      .map(([key, _]) => key);

    const resultFilters: RunSearchFilters = {
      ...this.data,
      direction: selectedDirections,
      cycles: selectedCycles,
      interfacialTensionMin: this.rangesValue.interfacialTensionRange.min ?? null,
      interfacialTensionMax: this.rangesValue.interfacialTensionRange.max ?? null,
      primaryDrainageMin: this.rangesValue.primaryDrainageRange.min ?? null,
      primaryDrainageMax: this.rangesValue.primaryDrainageRange.max ?? null,
      firstImbibitionMin: this.rangesValue.firstImbibitionRange.min ?? null,
      firstImbibitionMax: this.rangesValue.firstImbibitionRange.max ?? null,
    };

    this.dialogRef.close(resultFilters);
  }

  onCancel(): void {
    this.dialogRef.close(null);
  }

  checkIfFiltersValid(): boolean {
    return this.rangesForm.valid;
  }

  private initDirectionsForm(): void {
    this.directionsForm = this.fb.group({
      x: [this.data.direction.includes('x')],
      y: [this.data.direction.includes('y')],
      z: [this.data.direction.includes('z')],
    });
  }

  private initCyclesForm(): void {
    this.cyclesForm = this.fb.group({
      PD: [this.data.cycles.includes('PD')],
      PDFI: [this.data.cycles.includes('PDFI')],
    });
  }

  private initRangesForm(): void {
    this.rangesForm = this.fb.group({
      interfacialTensionRange: this.fb.group(
        {
          min: [this.data.interfacialTensionMin ?? null, [Validators.min(0)]],
          max: [this.data.interfacialTensionMax ?? null, [Validators.min(0)]],
        },
        { validators: this.rangeValidator('min', 'max') }
      ),
      primaryDrainageRange: this.fb.group(
        {
          min: [this.data.primaryDrainageMin ?? null, [Validators.min(-1), Validators.max(1)]],
          max: [this.data.primaryDrainageMax ?? null, [Validators.min(-1), Validators.max(1)]],
        },
        { validators: this.rangeValidator('min', 'max') }
      ),
      firstImbibitionRange: this.fb.group(
        {
          min: [this.data.firstImbibitionMin ?? null, [Validators.min(-1), Validators.max(1)]],
          max: [this.data.firstImbibitionMax ?? null, [Validators.min(-1), Validators.max(1)]],
        },
        { validators: this.rangeValidator('min', 'max') }
      ),
    });
  }

  private rangeValidator(minKey: string, maxKey: string): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const min = group.get(minKey)?.value;
      const max = group.get(maxKey)?.value;

      if (min == null || max == null) return null;

      return +min < +max ? null : { range: true };
    };
  }
}
